import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  collection, 
  query, 
  where, 
  getDocs,
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { 
  ref, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { USER_STATUS } from '../constants/authConstants';

const COLLECTIONS = {
  USERS: 'users'
};

class UserService {
  constructor() {
    this.collectionName = COLLECTIONS.USERS;
  }

  async createUser(uid, userData) {
    try {
      const userRef = doc(db, this.collectionName, uid);
      const userDoc = {
        ...userData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(userRef, userDoc);
      console.log('User document created successfully');
      return userDoc;
    } catch (error) {
      console.error('Error creating user:', error);
      throw new Error('Failed to create user profile');
    }
  }

  async adminCreateUser(userData) {
    try {
      const userDoc = {
        ...userData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(doc(db, this.collectionName, userData.uid), userDoc);
      console.log('User document created by admin');
      return userDoc;
    } catch (error) {
      console.error('Error creating user by admin:', error);
      throw new Error('Failed to create user profile');
    }
  }

  async getUserById(uid) {
    try {
      const userRef = doc(db, this.collectionName, uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        return {
          id: userSnap.id,
          ...userData,
          createdAt: userData.createdAt?.toDate() || null,
          updatedAt: userData.updatedAt?.toDate() || null,
          lastLoginAt: userData.lastLoginAt?.toDate() || null,
          approvedAt: userData.approvedAt?.toDate() || null
        };
      } else {
        throw new Error('User not found');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      throw new Error('Failed to fetch user data');
    }
  }

  async updateUser(uid, updateData) {
    try {
      const userRef = doc(db, this.collectionName, uid);
      const updates = {
        ...updateData,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(userRef, updates);
      console.log('User updated successfully');
      return updates;
    } catch (error) {
      console.error('Error updating user:', error);
      throw new Error('Failed to update user data');
    }
  }

  async updateUserLastLogin(uid) {
    try {
      const userRef = doc(db, this.collectionName, uid);
      await updateDoc(userRef, {
        lastLoginAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating last login:', error);
    }
  }

  async updateUserStatus(uid, status) {
    try {
      const updateData = {
        status,
        updatedAt: serverTimestamp()
      };

      switch (status) {
        case USER_STATUS.ACTIVE:
          updateData.activatedAt = serverTimestamp();
          break;
        case USER_STATUS.INACTIVE:
          updateData.deactivatedAt = serverTimestamp();
          break;
        case USER_STATUS.SUSPENDED:
          updateData.suspendedAt = serverTimestamp();
          break;
        default:
          break;
      }

      await this.updateUser(uid, updateData);
      console.log('User status updated successfully');
    } catch (error) {
      console.error('Error updating user status:', error);
      throw new Error('Failed to update user status');
    }
  }

  async updateProfilePicture(uid, profilePictureData) {
    try {
      const updateData = {
        profilePicture: profilePictureData.url,
        profilePictureFileName: profilePictureData.fileName,
        profilePictureUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await updateDoc(doc(db, this.collectionName, uid), updateData);
      console.log('Profile picture updated successfully');
      return updateData;
    } catch (error) {
      console.error('Error updating profile picture:', error);
      throw new Error('Failed to update profile picture');
    }
  }

  async removeProfilePicture(uid) {
    try {
      const userData = await this.getUserById(uid);
      
      if (userData.profilePictureFileName) {
        try {
          const fileRef = ref(storage, `profile-pictures/${userData.profilePictureFileName}`);
          await deleteObject(fileRef);
          console.log('Profile picture file deleted from storage');
        } catch (storageError) {
          console.warn('Could not delete profile picture from storage:', storageError);
        }
      }

      const updateData = {
        profilePicture: null,
        profilePictureFileName: null,
        profilePictureUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await updateDoc(doc(db, this.collectionName, uid), updateData);
      console.log('Profile picture removed from user document');
      return updateData;
    } catch (error) {
      console.error('Error removing profile picture:', error);
      throw new Error('Failed to remove profile picture');
    }
  }

  async deleteUser(uid) {
    try {
      const userData = await this.getUserById(uid);
      
      if (userData.profilePictureFileName) {
        try {
          const fileRef = ref(storage, `profile-pictures/${userData.profilePictureFileName}`);
          await deleteObject(fileRef);
          console.log('Profile picture deleted from storage during user deletion');
        } catch (storageError) {
          console.warn('Could not delete profile picture during user deletion:', storageError);
        }
      }

      const userRef = doc(db, this.collectionName, uid);
      await deleteDoc(userRef);
      console.log('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      throw new Error('Failed to delete user');
    }
  }

  async getUserByEmail(email) {
    try {
      const usersRef = collection(db, this.collectionName);
      const q = query(usersRef, where('email', '==', email.toLowerCase()));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const userData = userDoc.data();
        return {
          id: userDoc.id,
          ...userData,
          createdAt: userData.createdAt?.toDate() || null,
          updatedAt: userData.updatedAt?.toDate() || null,
          lastLoginAt: userData.lastLoginAt?.toDate() || null,
          approvedAt: userData.approvedAt?.toDate() || null
        };
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error fetching user by email:', error);
      throw new Error('Failed to fetch user data');
    }
  }

  async getAllUsers() {
    try {
      const usersRef = collection(db, this.collectionName);
      const q = query(usersRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const users = [];
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        users.push({
          id: doc.id,
          ...userData,
          createdAt: userData.createdAt?.toDate() || null,
          updatedAt: userData.updatedAt?.toDate() || null,
          lastLoginAt: userData.lastLoginAt?.toDate() || null,
          approvedAt: userData.approvedAt?.toDate() || null
        });
      });
      
      return users;
    } catch (error) {
      console.error('Error fetching all users:', error);
      throw new Error('Failed to fetch users');
    }
  }

  async getUsersByStatus(status) {
    try {
      const usersRef = collection(db, this.collectionName);
      const q = query(
        usersRef, 
        where('status', '==', status),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      const users = [];
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        users.push({
          id: doc.id,
          ...userData,
          createdAt: userData.createdAt?.toDate() || null,
          updatedAt: userData.updatedAt?.toDate() || null,
          lastLoginAt: userData.lastLoginAt?.toDate() || null,
          approvedAt: userData.approvedAt?.toDate() || null
        });
      });
      
      return users;
    } catch (error) {
      console.error('Error fetching users by status:', error);
      throw new Error('Failed to fetch users');
    }
  }

  async getUsersBySector(sector) {
    try {
      const usersRef = collection(db, this.collectionName);
      const q = query(
        usersRef, 
        where('sector', '==', sector),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      const users = [];
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        users.push({
          id: doc.id,
          ...userData,
          createdAt: userData.createdAt?.toDate() || null,
          updatedAt: userData.updatedAt?.toDate() || null,
          lastLoginAt: userData.lastLoginAt?.toDate() || null,
          approvedAt: userData.approvedAt?.toDate() || null
        });
      });
      
      return users;
    } catch (error) {
      console.error('Error fetching users by sector:', error);
      throw new Error('Failed to fetch users');
    }
  }

  async getUsersByDepartment(department) {
    try {
      const usersRef = collection(db, this.collectionName);
      const q = query(
        usersRef, 
        where('department', '==', department),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      const users = [];
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        users.push({
          id: doc.id,
          ...userData,
          createdAt: userData.createdAt?.toDate() || null,
          updatedAt: userData.updatedAt?.toDate() || null,
          lastLoginAt: userData.lastLoginAt?.toDate() || null,
          approvedAt: userData.approvedAt?.toDate() || null
        });
      });
      
      return users;
    } catch (error) {
      console.error('Error fetching users by department:', error);
      throw new Error('Failed to fetch users');
    }
  }

  async searchUsers(searchTerm, fields = ['fullName', 'email']) {
    try {
      const allUsers = await this.getAllUsers();
      const term = searchTerm.toLowerCase();
      
      return allUsers.filter(user => 
        fields.some(field => {
          const value = user[field];
          return value && value.toString().toLowerCase().includes(term);
        })
      );
    } catch (error) {
      console.error('Error searching users:', error);
      throw new Error('Failed to search users');
    }
  }

  async getUserStatistics() {
    try {
      const allUsers = await this.getAllUsers();
      
      const stats = {
        total: allUsers.length,
        byStatus: {
          [USER_STATUS.ACTIVE]: allUsers.filter(u => u.status === USER_STATUS.ACTIVE).length,
          [USER_STATUS.PENDING]: allUsers.filter(u => u.status === USER_STATUS.PENDING).length,
          [USER_STATUS.INACTIVE]: allUsers.filter(u => u.status === USER_STATUS.INACTIVE).length,
          [USER_STATUS.SUSPENDED]: allUsers.filter(u => u.status === USER_STATUS.SUSPENDED).length
        },
        bySector: {},
        byDepartment: {},
        recentRegistrations: {
          today: allUsers.filter(u => {
            const today = new Date();
            const userDate = new Date(u.createdAt);
            return userDate.toDateString() === today.toDateString();
          }).length,
          thisWeek: allUsers.filter(u => {
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            return new Date(u.createdAt) > weekAgo;
          }).length,
          thisMonth: allUsers.filter(u => {
            const monthAgo = new Date();
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return new Date(u.createdAt) > monthAgo;
          }).length
        }
      };

      allUsers.forEach(user => {
        if (user.sector) {
          stats.bySector[user.sector] = (stats.bySector[user.sector] || 0) + 1;
        }
      });

      allUsers.forEach(user => {
        if (user.department) {
          stats.byDepartment[user.department] = (stats.byDepartment[user.department] || 0) + 1;
        }
      });
      
      return stats;
    } catch (error) {
      console.error('Error getting user statistics:', error);
      throw new Error('Failed to get user statistics');
    }
  }

  // UPDATED: Validate with fullName
  validateUserData(userData) {
    const errors = {};
    
    if (!userData.fullName || userData.fullName.trim().length < 2) {
      errors.fullName = 'Full name must be at least 2 characters';
    }
    
    if (!userData.email || !/\S+@\S+\.\S+/.test(userData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!userData.sector) {
      errors.sector = 'Please select a sector';
    }
    
    if (!userData.department) {
      errors.department = 'Please select a department';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  async getUsersWithCommittees() {
    try {
      const users = await this.getAllUsers();
      return users.map(user => ({
        ...user,
        committees: user.committees || []
      }));
    } catch (error) {
      console.error('Error fetching users with committees:', error);
      throw new Error('Failed to fetch users with committee data');
    }
  }

  async updateUserCommittees(uid, committees) {
    try {
      const updateData = {
        committees: committees || [],
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(doc(db, this.collectionName, uid), updateData);
      console.log('User committees updated successfully');
      return updateData;
    } catch (error) {
      console.error('Error updating user committees:', error);
      throw new Error('Failed to update user committees');
    }
  }

  async removeCommitteeFromAllUsers(committeeId) {
    try {
      const users = await this.getAllUsers();
      const batch = writeBatch(db);
      
      users.forEach(user => {
        if (user.committees && user.committees.includes(committeeId)) {
          const userRef = doc(db, this.collectionName, user.id);
          const updatedCommittees = user.committees.filter(id => id !== committeeId);
          
          batch.update(userRef, {
            committees: updatedCommittees,
            updatedAt: serverTimestamp()
          });
        }
      });
      
      await batch.commit();
      console.log('Committee removed from all users');
    } catch (error) {
      console.error('Error removing committee from users:', error);
      throw new Error('Failed to remove committee from users');
    }
  }
}

export const userService = new UserService();